import { useState } from "react";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import type { Country } from "@/components/country-select";
import {
	type EditingPerson,
	EditingPersonItem,
} from "@/components/editing-person-item";
import {
	PERSON_FIELD_LABELS,
	type PersonFormData,
	PersonFormFields,
	type PersonFormFieldsWarnings,
	type SelectedPerson,
} from "@/components/person-form-fields";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { SheetFooter } from "@/components/ui/sheet";

interface Person {
	_key: string;
	physical_person: PersonFormData;
	is_ubo: boolean;
	is_statutory_member: boolean;
	other_role: string | null;
}

interface PersonFormProps {
	person: Person;
	countries: Country[];
	errors?: Partial<Record<string, string>>;
	initialEditingPerson?: EditingPerson;
	warnings?: PersonFormFieldsWarnings;
	children?: React.ReactNode;
	footerStart?: React.ReactNode;
	mode: "create" | "update";
	onSave: (person: Person, editingPerson?: EditingPerson) => void;
	onCancel: () => void;
	onClearError?: (field: keyof PersonFormData) => void;
}

function PersonForm({
	person,
	countries,
	errors = {},
	initialEditingPerson,
	warnings,
	children,
	footerStart,
	mode,
	onSave,
	onCancel,
	onClearError,
}: PersonFormProps) {
	const [formData, setFormData] = useState<Person>({ ...person });
	const [editingPerson, setEditingPerson] = useState<EditingPerson | undefined>(
		initialEditingPerson,
	);
	const [pendingPerson, setPendingPerson] = useState<{
		conflicts: Conflict[];
		mergedValues: Partial<PersonFormData>;
		selected: SelectedPerson;
	}>();

	const handlePersonChange = (data: PersonFormData) => {
		if (localErrors["physical_person.first_name"]) {
			localErrors["physical_person.first_name"] = undefined;
		}
		if (localErrors["physical_person.last_name"]) {
			localErrors["physical_person.last_name"] = undefined;
		}
		setFormData({ ...formData, physical_person: data });
	};

	const handlePersonSelected = (selected: SelectedPerson) => {
		setEditingPerson(selected);

		const conflicts = findConflicts(
			formData.physical_person,
			selected.formData,
			PERSON_FIELD_LABELS,
			{ country: "CZ" },
		);

		if (conflicts.length > 0) {
			const mergedValues = mergeValues(
				formData.physical_person,
				selected.formData,
			);
			setPendingPerson({ conflicts, mergedValues, selected });
		} else {
			setFormData((prev) => ({
				...prev,
				physical_person: selected.formData,
			}));
		}
	};

	const handleApplyPerson = () => {
		if (!pendingPerson) return;
		setFormData((prev) => ({
			...prev,
			physical_person: pendingPerson.selected.formData,
		}));
		setPendingPerson(undefined);
	};

	const handleCancelPerson = () => {
		setPendingPerson(undefined);
	};

	const [localErrors, setLocalErrors] = useState<
		Partial<Record<string, string>>
	>({});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const validationErrors: Partial<Record<string, string>> = {};
		if (!formData.physical_person.first_name.trim()) {
			validationErrors["physical_person.first_name"] = "Jméno je povinné";
		}
		if (!formData.physical_person.last_name.trim()) {
			validationErrors["physical_person.last_name"] = "Příjmení je povinné";
		}
		if (Object.keys(validationErrors).length > 0) {
			setLocalErrors(validationErrors);
			return;
		}
		setLocalErrors({});
		onSave(formData, editingPerson);
	};

	const fieldErrors = { ...localErrors, ...errors };

	return (
		<form
			onSubmit={handleSubmit}
			className="flex flex-1 flex-col overflow-hidden"
		>
			<div className="space-y-4 overflow-y-auto p-4">
				{children}
				{editingPerson && (
					<EditingPersonItem
						person={editingPerson}
						onCancel={() => {
							setEditingPerson(undefined);
						}}
						variant={mode === "create" ? "link_to_business" : "merge"}
					/>
				)}

				<PersonFormFields
					value={formData.physical_person}
					onChange={handlePersonChange}
					errors={fieldErrors}
					countries={countries}
					onPersonSelected={handlePersonSelected}
					birthNumberRequired={false}
					onClearError={onClearError}
					warnings={warnings}
					excludePersonId={initialEditingPerson?.id}
				/>

				<Card>
					<CardHeader>
						<CardTitle>Role</CardTitle>
					</CardHeader>
					<CardContent>
						<FieldGroup>
							<Field orientation="horizontal">
								<Checkbox
									id="is_ubo"
									checked={formData.is_ubo}
									onCheckedChange={(checked) =>
										setFormData({ ...formData, is_ubo: checked === true })
									}
								/>
								<FieldLabel htmlFor="is_ubo">Skutečný majitel (UBO)</FieldLabel>
							</Field>
							<Field orientation="horizontal">
								<Checkbox
									id="is_statutory_member"
									checked={formData.is_statutory_member}
									onCheckedChange={(checked) =>
										setFormData({
											...formData,
											is_statutory_member: checked === true,
										})
									}
								/>
								<FieldLabel htmlFor="is_statutory_member">
									Člen statutárního orgánu
								</FieldLabel>
							</Field>
							<Field orientation="horizontal">
								<Checkbox
									id="has_other_role"
									checked={formData.other_role !== null}
									onCheckedChange={(checked) =>
										setFormData({
											...formData,
											other_role: checked === true ? "" : null,
										})
									}
								/>
								<FieldLabel htmlFor="has_other_role">Ostatní</FieldLabel>
							</Field>
							{formData.other_role !== null && (
								<Field>
									<Input
										id="other_role"
										value={formData.other_role}
										onChange={(e) =>
											setFormData({ ...formData, other_role: e.target.value })
										}
										placeholder="Např. jednatel, prokurista..."
									/>
								</Field>
							)}
						</FieldGroup>
					</CardContent>
				</Card>
			</div>

			<SheetFooter className="flex flex-row items-center justify-between gap-2 border-t px-4 lg:px-6">
				<div>{footerStart}</div>
				<div className="flex gap-2">
					<Button type="button" variant="outline" size="lg" onClick={onCancel}>
						Zrušit
					</Button>
					<Button type="submit" size="lg">
						{mode === "create"
							? "Přidat osobu"
							: editingPerson
								? "Sloučit osoby"
								: "Uložit změny"}
					</Button>
				</div>
			</SheetFooter>

			{pendingPerson && (
				<ConflictDialog
					conflicts={pendingPerson.conflicts}
					description="Vybraná osoba má odlišné údaje."
					onApply={handleApplyPerson}
					onCancel={handleCancelPerson}
				/>
			)}
		</form>
	);
}

export { PersonForm, type Person, type PersonFormProps };
